// 組織管理 › 組織設定（/org，舊 /settings/org 轉址；SPEC #vms-web-845、#org-model-create、#org-model-membership、#org-model-policy、ORG-01..05、AUTH-04；
// VMS UX 評審 2026-10-13 P1-10、P0-3）：
//   - 我的組織：個人組織（帳號自帶，不能離開 / 轉移 / 刪除）與我參加的 team 組織；〔建立組織〕開對話框（每人最多 5 個，個人組織不算；?create=1 直接打開，
//     範圍切換器面板底部的〔建立組織〕到這裡）、離開組織
//   - 目前的組織（= 頁首範圍所在的組織，組織膠囊切換；「全部」時先問）：基本資料、擁有權移轉（team，要 step-up）、刪除（team，要先沒有家戶）
//   - 層級名稱（owner / org_admin）；組織結構（節點、家戶、指派、中斷繼承）在「組織與場域」（/sites）
//   - 安全政策：要求兩步驟驗證、登入時效、閒置、同時工作階段、IP（只在進入這個組織的資源時套用）
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { isKinioError } from '@kinio/web-sdk';
import { useApp } from '../../lib/AppContext';
import { useScope } from '../../lib/scope';
import { hasOrgPerm, isOrgAdmin, orgApi, orgsApi, orgUsable, type MyOrg } from '../../lib/planeApi';
import { Dialog } from '../../components/Dialog';
import { useConfirm } from '../../components/ConfirmDialog';
import { OrgGate, orgName, relevantOrgs } from '../shell/OrgGate';
import { memberName } from '../people/MembersTab';
import { SsoCard } from './SsoCard';
import { SecurityPolicyCard } from './SecurityPolicyCard';
import { LevelSettingsDialog } from '../orgtree/StructureDialogs';
import { useTreeSettings } from '../orgtree/hooks';
import { leafText, levelList, levelText } from '../../lib/levels';
import { DevicePolicyCard } from './DevicePolicyCard';
import { AlarmSlaCard } from './AlarmSlaCard';
import { KmgrOrgCard } from '../kmgr/entry';
import { OrgEnergyCard } from '../energy/OrgTariffs';

export function errText(t: (k: string, o?: Record<string, unknown>) => string, e: unknown): string {
  const code = isKinioError(e) ? e.code : 'default';
  return t(`org.err_${code}`, { defaultValue: t('org.err_default', { code }) });
}

export function OrgPage() {
  const { t } = useTranslation();
  const { orgs, homes } = useScope();
  const usable = relevantOrgs(orgs, homes).filter(orgUsable);
  return (
    <div className="page">
      <h1 className="page-title">{t('nav.org')}</h1>
      <OrgGate candidates={usable}>
        {(current) => (
          <>
            <OrgInfoCard key={current.org_id} org={current} part="info" />
            <SecurityPolicyCard org={current} />
            <SsoCard key={'sso' + current.org_id} org={current} />
            <DevicePolicyCard org={current} />
            {current.kind === 'team' && <AlarmSlaCard org={current} />}
            <KmgrOrgCard org={current} />
            <OrgEnergyCard org={current} />
            {isOrgAdmin(current) && <LevelNamesCard key={'lv' + current.org_id} org={current} />}
            {/* 「我的組織」在維護之前，維護（轉移擁有權、刪除組織）真的在頁底（UX-03；第三輪 B3-23、第四輪 R4-P1-8） */}
            <MyOrgsCard orgs={orgs} />
            <OrgInfoCard key={'m' + current.org_id} org={current} part="maintenance" />
          </>
        )}
      </OrgGate>
      {usable.length === 0 && <MyOrgsCard orgs={orgs} />}
    </div>
  );
}

/** 層級名稱（SPEC #org-tree-model、#vms-web-845；從組織與場域頁搬來，VMS UX 評審 2026-10-13 P1-7）：只有 owner / org_admin 能改。 */
function LevelNamesCard({ org }: { org: MyOrg }) {
  const { t } = useTranslation();
  const settings = useTreeSettings(org);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const qc = useQueryClient();
  const levels = levelList(settings.data);
  return (
    <section className="card" data-testid="level-names">
      <div className="toolbar">
        <h2 className="section-title">{t('ot.level_settings')}</h2>
        <div className="spacer" />
        <button className="btn btn-sm" onClick={() => setOpen(true)}>
          {t('common.edit')}
        </button>
      </div>
      <p className="muted small">{t('org.levels_hint')}</p>
      <p className="small">{[...levels.map((l) => levelText(t, l)), leafText(t, settings.data, org.kind === 'personal')].join(' › ')}</p>
      {notice && <p className="notice small">{notice}</p>}
      {open && (
        <LevelSettingsDialog
          org={org}
          settings={settings.data ?? null}
          close={() => setOpen(false)}
          done={(m) => (setNotice(m), setOpen(false), void qc.invalidateQueries({ queryKey: ['ot', org.org_id] }))}
        />
      )}
    </section>
  );
}

/** 我的組織：個人組織第一筆；角色、要求 MFA 等狀態；離開（owner 不能離開）；另建 team 組織。 */
function MyOrgsCard({ orgs }: { orgs: MyOrg[] }) {
  const { t } = useTranslation();
  const { setScope } = useScope();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const [err, setErr] = useState<string | null>(null);
  const leave = useMutation({
    mutationFn: (o: MyOrg) => orgApi.removeMember(o, 'me'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-orgs'] });
      void qc.invalidateQueries({ queryKey: ['me-devices'] });
    },
    onError: (e) => setErr(errText(t, e)),
  });
  return (
    <section className="card" data-testid="my-orgs">
      <h2 className="section-title">{t('org.my_orgs')}</h2>
      <p className="muted small">{t('org.my_orgs_hint')}</p>
      {err && <p className="error">{err}</p>}
      <div className="table-wrap">
        <table className="grid">
          <thead>
            <tr>
              <th>{t('org.name')}</th>
              <th>{t('org.kind_col')}</th>
              <th>{t('people.role')}</th>
              <th>{t('people.status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {orgs.map((o) => (
              <tr key={o.org_id} data-org={o.org_id}>
                <td>{o.display_name}</td>
                <td>{t(`org.kind_${o.kind}`)}</td>
                <td>{t(`people.org_role_${o.role}`, { defaultValue: o.role })}</td>
                <td className="small">
                  {o.notice ? (
                    <span className="badge badge-warn">{t(`scope.notice_${o.notice}`, { defaultValue: o.notice })}</span>
                  ) : o.mfa_required ? (
                    <span className="badge">{t('org.requires_mfa')}</span>
                  ) : (
                    '—'
                  )}
                </td>
                <td>
                  <div className="row">
                    {orgUsable(o) && (isOrgAdmin(o) || hasOrgPerm(o, 'org.sites')) && (
                      <button className="btn btn-sm" onClick={() => setScope('org:' + o.org_id, '', { announce: t('scope.switched_to', { name: orgName(t, o) }) })}>
                        {t('org.manage')}
                      </button>
                    )}
                    {o.role !== 'owner' && (
                      <button
                        className="btn btn-sm"
                        disabled={leave.isPending}
                        onClick={async () => (await ask({ danger: true, title: t('org.leave_confirm', { name: o.display_name }), body: t('org.leave_body'), action: t('org.leave') })) && leave.mutate(o)}
                      >
                        {t('org.leave')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CreateOrg />
      {dialog}
    </section>
  );
}

function CreateOrg() {
  const { t } = useTranslation();
  const { planes, setScope } = useScope();
  const { config } = useApp();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const [open, setOpenState] = useState(sp.get('create') === '1');
  const setOpen = (v: boolean) => {
    setOpenState(v);
    if (!v && sp.has('create')) {
      const n = new URLSearchParams(sp);
      n.delete('create');
      setSp(n, { replace: true });
    }
  };
  const [name, setName] = useState('');
  const [created, setCreated] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: () => orgsApi.create(planes.find((p) => p.env === config.env) ?? planes[0], name.trim()),
    onSuccess: (r) => {
      setCreated(r.org.display_name);
      setName('');
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ['me-orgs'] }).then(() => setScope('org:' + r.org.org_id, '', { announce: t('scope.switched_to', { name: r.org.display_name }) }));
      void qc.invalidateQueries({ queryKey: ['me-devices'] });
    },
  });
  return (
    <div style={{ marginTop: 12 }}>
      {created && <p className="notice">{t('org.created', { name: created })}</p>}
      <button className="btn" onClick={() => setOpen(true)} data-testid="org-create-open">
        {t('org.create_title')}
      </button>
      <Dialog open={open} title={t('org.create_title')} onClose={() => setOpen(false)}>
        <p className="muted small">{t('org.create_body')}</p>
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <input className="input" required minLength={2} maxLength={60} placeholder={t('org.name')} value={name} onChange={(e) => setName(e.target.value)} aria-label={t('org.name')} autoFocus />
          {create.isError && <p className="error">{errText(t, create.error)}</p>}
          <div className="row">
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={create.isPending || !planes.length}>
              {t('org.create')}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

/** 組織基本資料、擁有權移轉、刪除組織（team 組織的 owner；要 5 分鐘內重新驗證）。 */
function OrgInfoCard({ org, part }: { org: MyOrg; part: 'info' | 'maintenance' }) {
  const { t } = useTranslation();
  const { ask, dialog } = useConfirm();
  const qc = useQueryClient();
  const { setScope } = useScope();
  const [transfer, setTransfer] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [blockingHomes, setBlockingHomes] = useState<string[] | null>(null);
  const del = useMutation({
    mutationFn: () => orgsApi.remove(org),
    onSuccess: () => {
      setScope('all');
      void qc.invalidateQueries({ queryKey: ['me-orgs'] });
      void qc.invalidateQueries({ queryKey: ['me-devices'] });
    },
    onError: (e) => {
      if (isKinioError(e) && e.code === 'ORG_NOT_EMPTY') {
        const homes = (e.detail?.homes ?? []) as unknown[];
        setBlockingHomes(homes.map((h) => (typeof h === 'string' ? h : String((h as { display_name?: string; home_id?: string }).display_name ?? (h as { home_id?: string }).home_id ?? ''))));
        return;
      }
      setErr(errText(t, e));
    },
  });
  const team = org.kind === 'team';
  if (part === 'maintenance') {
    if (!(team && org.role === 'owner')) return null;
    return (
      <section className="card maint-zone" data-testid="org-maintenance">
        <h2 className="section-title" style={{ marginTop: 0 }}>
          {t('ddev.maintenance')}
        </h2>
        {blockingHomes && (
          <div className="notice">
            <p>{t('org.err_ORG_NOT_EMPTY')}</p>
            <ul className="plain-list">
              {blockingHomes.map((h) => (
                <li key={h}>{h}</li>
              ))}
            </ul>
          </div>
        )}
        {err && <p className="error">{err}</p>}
        <div className="row">
          <button className="btn btn-sm" onClick={() => setTransfer(true)}>
            {t('org.owner_transfer')}
          </button>
          <button
            className="btn btn-sm btn-danger"
            disabled={del.isPending}
            onClick={async () => (await ask({ title: t('org.delete_confirm', { name: org.display_name }), body: t('org.delete_body'), action: t('org.delete'), typeToConfirm: org.display_name })) && del.mutate()}
          >
            {t('org.delete')}
          </button>
        </div>
        <OrgOwnerTransferDialog org={org} open={transfer} close={() => setTransfer(false)} />
        {dialog}
      </section>
    );
  }
  return (
    <section className="card" data-testid="org-info">
      <div className="toolbar">
        <h2 className="section-title">{org.display_name}</h2>
        <span className="chip">{t(`org.kind_${org.kind}`)}</span>
        <div className="spacer" />
        {isOrgAdmin(org) && (
          <button className="btn btn-sm" onClick={() => setRenaming(true)} data-testid="org-rename">
            {t('org.rename')}
          </button>
        )}
      </div>
      <p className="muted small">{t(team ? 'org.team_hint' : 'org.personal_hint')}</p>
      <p className="small">
        {t('people.role')}
        {t('common.colon')}
        {t(`people.org_role_${org.role}`, { defaultValue: org.role })}
        {org.member_count !== undefined && <span className="muted"> · {t('org.member_count', { count: org.member_count })}</span>}
      </p>
      {err && <p className="error">{err}</p>}
      <OrgRenameDialog org={org} open={renaming} close={() => setRenaming(false)} />
      {dialog}
    </section>
  );
}

/** 改組織名稱（PATCH /v1/orgs/{org}，owner / org_admin；平台 H 起才有，舊後端回 404 / 405 → 顯示「還沒開放」）。 */
/** 改名成功後直接改快取裡的組織名稱（「我的組織」與「我的設備」的 orgs[]）：卡片、頁首膠囊、範圍切換器立刻換新名，背景再重新抓。 */
export function applyOrgRename(qc: QueryClient, orgId: string, displayName: string) {
  const rename = <O extends { org_id: string; display_name: string }>(os: O[]) => os.map((o) => (o.org_id === orgId ? { ...o, display_name: displayName } : o));
  qc.setQueriesData<MyOrg[]>({ queryKey: ['me-orgs'] }, (old) => (Array.isArray(old) ? rename(old) : old));
  qc.setQueriesData<{ orgs?: { org_id: string; display_name: string }[] }>({ queryKey: ['me-devices'] }, (old) =>
    old && Array.isArray(old.orgs) ? { ...old, orgs: rename(old.orgs) } : old,
  );
}

function OrgRenameDialog({ org, open, close }: { org: MyOrg; open: boolean; close(): void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [forOpen, setForOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (open !== forOpen) {
    setForOpen(open);
    setName(org.display_name);
    setErr(null);
  }
  const save = useMutation({
    mutationFn: () => orgsApi.rename(org, name.trim()),
    onSuccess: (r) => {
      applyOrgRename(qc, org.org_id, r?.org?.display_name || name.trim()); // 先換畫面上的名稱，不等重新抓（dev 跨洋延遲時存完還是舊名）
      void qc.invalidateQueries({ queryKey: ['me-orgs'] });
      void qc.invalidateQueries({ queryKey: ['me-devices'] });
      close();
    },
    onError: (e) => {
      const old = isKinioError(e) && (e.status === 405 || (e.status === 404 && e.code !== 'ORG_NOT_FOUND'));
      setErr(old ? t('org.err_FEATURE_DISABLED') : errText(t, e));
    },
  });
  return (
    <Dialog open={open} title={t('org.rename_of', { name: org.display_name })} onClose={close}>
      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setErr(null);
          save.mutate();
        }}
      >
        <label className="field">
          <span>{t('org.name')}</span>
          <input className="input" required minLength={2} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        {err && <div className="error">{err}</div>}
        <div className="row-end">
          <button type="button" className="btn" onClick={close}>
            {t('common.cancel')}
          </button>
          <button className="btn btn-primary" disabled={save.isPending || name.trim() === org.display_name}>
            {t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** 組織擁有權移轉（team）：對象要是在職的管理員；原 owner 變管理員；通知雙方與其他管理員。 */
function OrgOwnerTransferDialog({ org, open, close }: { org: MyOrg; open: boolean; close(): void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const members = useQuery({ queryKey: ['org-members', org.org_id], queryFn: () => orgApi.members(org), enabled: open });
  const admins = (members.data ?? []).filter((m) => m.role === 'org_admin' && m.status === 'active' && !m.is_me);
  const [target, setTarget] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const save = useMutation({
    mutationFn: async () => {
      const fresh = await orgsApi.get(org); // expected_version = orgs.authz_version，別人剛改過就 409
      return orgsApi.ownerTransfer(org, target, fresh.version);
    },
    onSuccess: () => {
      setDone(true);
      void qc.invalidateQueries({ queryKey: ['me-orgs'] });
      void qc.invalidateQueries({ queryKey: ['org-members', org.org_id] });
    },
    onError: (e) => setErr(errText(t, e)),
  });
  function reset() {
    setTarget('');
    setErr(null);
    setDone(false);
    close();
  }
  return (
    <Dialog open={open} title={t('org.owner_transfer_of', { name: org.display_name })} onClose={reset}>
      {done ? (
        <div className="form">
          <p className="notice">{t('org.owner_transfer_done')}</p>
          <button className="btn btn-primary" onClick={reset}>
            {t('common.done')}
          </button>
        </div>
      ) : (
        <form
          className="form"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            setErr(null);
            save.mutate();
          }}
        >
          <p className="muted small">{t('org.owner_transfer_hint')}</p>
          <label className="field">
            <span>{t('org.new_owner')}</span>
            <select className="input" required value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">—</option>
              {admins.map((m) => (
                <option key={m.user_id} value={m.user_id}>
                  {memberName(m)}
                </option>
              ))}
            </select>
            {members.data && admins.length === 0 && <span className="small muted">{t('org.no_admins')}</span>}
          </label>
          {err && <div className="error">{err}</div>}
          <div className="row-end">
            <span className="small muted">{t('people.stepup_note')}</span>
            <div className="spacer" />
            <button type="button" className="btn" onClick={reset}>
              {t('common.cancel')}
            </button>
            <button className="btn btn-primary" disabled={save.isPending || !target}>
              {t('org.owner_transfer')}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
